// Captain memorial persistence (spec-captain §Runtime). Death is permanent:
// on game over the captain's DNA — identity, run stats, death line — is
// appended to la.captains.memorial (cap 20, newest kept). Corrupt-safe in
// the same style as ui/storage.ts: bad JSON or a wrong shape reads as an
// empty memorial and quota errors are swallowed.

import type { CaptainRecord } from './seed.js';
import type { PronounSet, Temperament } from './dictionary.js';

export interface MemorialEntry {
  seed: number;
  name: string;
  pronouns: PronounSet;
  temperament: Temperament;
  wave: number;
  kills: number;
  seconds: number;
  deathLine: string;
  /** Epoch ms when the captain was lost. */
  at: number;
}

export interface CaptainRunStats {
  wave: number;
  kills: number;
  seconds: number;
}

const MEMORIAL_KEY = 'la.captains.memorial';
const MEMORIAL_CAP = 20;

const TEMPERAMENTS: readonly Temperament[] = ['stoic', 'hothead', 'smooth', 'worrier', 'deadpan'];
const PRONOUN_SETS: readonly PronounSet[] = ['he', 'she', 'they'];

/** Append the fallen captain to the memorial (cap 20, newest survive). */
export function saveCaptainDNA(
  record: CaptainRecord,
  run: CaptainRunStats,
  deathLine: string
): void {
  try {
    const entry: MemorialEntry = {
      seed: record.seed,
      name: record.name,
      pronouns: record.pronouns,
      temperament: record.temperament,
      wave: run.wave,
      kills: run.kills,
      seconds: run.seconds,
      deathLine,
      at: Date.now()
    };
    const list = loadMemorial();
    list.push(entry);
    localStorage.setItem(MEMORIAL_KEY, JSON.stringify(list.slice(-MEMORIAL_CAP)));
  } catch {
    // Ignore quota / privacy-mode errors (same policy as ui/storage.ts).
  }
}

/** Read the memorial, oldest first. Corrupt or missing data reads as []. */
export function loadMemorial(): MemorialEntry[] {
  try {
    const raw = localStorage.getItem(MEMORIAL_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isMemorialEntry);
  } catch {
    return [];
  }
}

function isMemorialEntry(v: unknown): v is MemorialEntry {
  if (typeof v !== 'object' || v === null) return false;
  const e = v as Record<string, unknown>;
  return (
    typeof e.seed === 'number' &&
    typeof e.name === 'string' &&
    PRONOUN_SETS.includes(e.pronouns as PronounSet) &&
    TEMPERAMENTS.includes(e.temperament as Temperament) &&
    typeof e.wave === 'number' &&
    typeof e.kills === 'number' &&
    typeof e.seconds === 'number' &&
    typeof e.deathLine === 'string'
  );
}
